import { Clock, List, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTranslation } from 'react-i18next'
import { useUser } from '@/app/store/auth'
import { TimezoneDisplayMode } from '@/utils/timezoneAdvanced'

interface TimezoneDisplayToggleProps {
    displayMode: TimezoneDisplayMode
    onDisplayModeChange: (mode: TimezoneDisplayMode) => void
}

export function TimezoneDisplayToggle({ displayMode, onDisplayModeChange }: TimezoneDisplayToggleProps) {
    const { t } = useTranslation()
    const user = useUser()

    const handleModeChange = (mode: TimezoneDisplayMode) => {
        onDisplayModeChange(mode)
        // Store in localStorage only, don't update user profile
        localStorage.setItem('timezoneDisplayMode', mode)
    }

    const getIcon = (mode: TimezoneDisplayMode) => {
        // Always show clock icon for the main button
        return <Clock className="h-4 w-4" />
    }

    const getCurrentLabel = () => {
        switch (displayMode) {
            case 'local':
                return t('header.local')
            case 'timezone':
                return t('header.userTimezone')
            case 'utc':
                return t('header.utc')
            default:
                return t('header.utc')
        }
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    {getIcon(displayMode)}
                    <span className="sr-only">{t('header.timezoneDisplay')}</span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem
                    onClick={() => handleModeChange('local')}
                    className={displayMode === 'local' ? 'bg-accent' : ''}
                >
                    <MapPin className="mr-2 h-4 w-4" />
                    <div className="flex flex-col">
                        <span className="font-medium">{t('header.local')}</span>
                        <span className="text-xs text-muted-foreground">
                            {t('header.localDescription')}
                        </span>
                    </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => handleModeChange('timezone')}
                    className={displayMode === 'timezone' ? 'bg-accent' : ''}
                >
                    <List className="mr-2 h-4 w-4" />
                    <div className="flex flex-col">
                        <span className="font-medium">{t('header.userTimezone')}</span>
                        <span className="text-xs text-muted-foreground">
                            {user?.timezone || t('header.utc')}
                        </span>
                    </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={() => handleModeChange('utc')}
                    className={displayMode === 'utc' ? 'bg-accent' : ''}
                >
                    <Clock className="mr-2 h-4 w-4" />
                    <div className="flex flex-col">
                        <span className="font-medium">{t('header.utc')}</span>
                        <span className="text-xs text-muted-foreground">
                            {t('header.utcDescription')}
                        </span>
                    </div>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
